'use client';

import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import { type BlogPost } from '@/features/marketing/constants/blog-posts';
import LandingNavbar from './LandingNavbar';
import FooterSection from './FooterSection';
import ScrollReveal from '@/features/preview/components/shared/ScrollReveal';

interface BlogIndexPageProps {
  posts: BlogPost[];
}

export default function BlogIndexPage({ posts }: BlogIndexPageProps) {
  const { t } = useLocale();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <LandingNavbar surface="dark" />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-xs font-medium uppercase tracking-wider mb-6">
              CMG Workflow CRM Blog
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent mb-4">
              {t({ en: 'Insights & Guides', ro: 'Ghiduri si Resurse' })}
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t({
                en: 'Learn how to automate your agency, manage leads better, and close more deals with CMG Workflow CRM.',
                ro: 'Afla cum sa iti automatizezi agentia, sa gestionezi mai bine lead-urile si sa inchizi mai multe contracte cu CMG Workflow CRM.',
              })}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Posts grid */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500">
                {t({
                  en: 'No posts yet. Check back soon.',
                  ro: 'Niciun articol inca. Revino curand.',
                })}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, index) => (
                <ScrollReveal key={post.slug} delay={index * 60}>
                  <article className="group h-full rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 hover:bg-white/[0.03] transition-all duration-200 flex flex-col">
                    <div className="p-6 flex flex-col flex-1">
                      {/* Category */}
                      <div className="mb-3">
                        <span className="px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-500 text-xs font-medium uppercase tracking-wider">
                          {t(post.category)}
                        </span>
                      </div>

                      {/* Title */}
                      <h2 className="text-lg font-semibold text-white group-hover:text-amber-50 transition-colors mb-2 leading-snug">
                        {t(post.title)}
                      </h2>

                      {/* Description */}
                      <p className="text-sm text-gray-400 leading-relaxed flex-1 mb-4">
                        {t(post.description)}
                      </p>

                      {/* Footer row */}
                      <div className="flex items-center justify-between pt-4 border-t border-white/5">
                        <time className="text-xs text-gray-600">
                          {new Date(post.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </time>
                        <Link
                          href={`/blog/${post.slug}`}
                          prefetch={false}
                          className={cn(
                            'inline-flex items-center gap-1 text-xs font-medium text-amber-500',
                            'hover:text-amber-400 transition-colors'
                          )}
                        >
                          {t({ en: 'Read more', ro: 'Citeste mai mult' })}
                          <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </article>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
